import React, { useEffect, useState, useRef } from "react";
import { View, ScrollView, Pressable, Platform, Modal } from "react-native";
import tw from "twrnc";
import Text from "@/components/shared/AppText";
import Callout from "@/components/settings/Callout";
import { SettingsPage } from "@/components/settings/SettingsPage";
import { text } from "@/components/settings/theme";
import { useEventAuth } from "@/components/teams/useWorkspaceAuth";
import AppActivityIndicator from "@/components/shared/AppActivityIndicator";
import PDFExportButton from "./PDFExportButton";
import { makeUrl } from "@/components/globalUtils";
import AppBrand from "@/components/shared/AppBrand";
import { videoStatsSimple } from "@/components/http/events";
import {
  ViewerHoursUsage,
  useEventViewerHours,
} from "@/components/viewerHours/EventViewerHours";
import EventReport from "@/components/viewerHours/EventReport";
import { lastUpdated } from "@/components/viewerHours/logic";
import { PRINT_WIDTH } from "@/components/viewerHours/pdfPages";
import { PDF_NEXT } from "@/components/viewerHours/reportParts";
import { t } from "@/localization";

// Event Analytics (business accounts): our own report, kept for good
// (components/viewerHours/EventReport.js), under the videographer's
// viewer-hours card (not in the PDF). While the PDF is made (`printing`) the
// report takes the page's width and its print layout, under a spinner.

const EventAnalytics = ({ event }) => {
  // Unique viewers (eventliveprod-event-viewers): undefined while loading.
  const [stats, setStats] = useState(undefined);
  // Teams: the account the event is in (yours, or the one you work in).
  const { broadcaster, custom_domain } = useEventAuth(event);
  const contentRef = useRef(null);
  const [printing, setPrinting] = useState(false);
  const viewerHours = useEventViewerHours(event);

  useEffect(() => {
    if (!event || !broadcaster) return;
    let current = true;
    videoStatsSimple(event.broadcaster || broadcaster, event.slug).then(
      (s) => current && setStats(s || null)
    );
    return () => {
      current = false;
    };
  }, [event?.id, broadcaster]);

  if (!event || stats === undefined || viewerHours.loading) {
    return <AppActivityIndicator />;
  }

  const url = makeUrl(event, custom_domain);
  const updatedAt = viewerHours.data?.updated_at;

  return (
    <ScrollView style={tw`flex-1`}>
      <View>
        <SettingsPage
          title={t("Analytics")}
          help="analytics.overview"
          description={t("Updated hourly.")}
          right={
            <PDFExportButton
              event={event}
              contentRef={contentRef}
              onPrinting={setPrinting}
            />
          }
        />
        <ViewerHoursUsage event={event} state={viewerHours} style={tw`mb-6`} />

        {/* The report (exported as the PDF) */}
        <View
          ref={contentRef}
          style={[tw`bg-white p-4`, printing && { width: PRINT_WIDTH }]}
        >
          <Text dataSet={PDF_NEXT} style={[text.sectionTitle, tw`mb-1`]}>
            {event.title}
          </Text>
          <View dataSet={PDF_NEXT} style={tw`mb-4`}>
            <Pressable
              onPress={() => {
                if (Platform.OS === "web") {
                  window.open(url, "_blank");
                }
              }}
              style={({ pressed }) => [
                tw`flex-row items-center`,
                { opacity: pressed ? 0.7 : 1 },
              ]}
            >
              <Text
                style={tw`text-[#F84123] underline font-bold`}
                numberOfLines={1}
              >
                {url.replace("https://", "")}
              </Text>
            </Pressable>
            {printing && !!updatedAt && (
              <Text style={[text.hint, tw`mt-1`]}>
                {t("Last updated {date}, {time}", lastUpdated(updatedAt))}
              </Text>
            )}
          </View>

          {stats === null && (
            <Callout tone="danger" style={tw`mb-4`}>
              {t("Sorry, there's been an error loading the analytics data.")}
            </Callout>
          )}

          <EventReport
            event={event}
            viewerHours={viewerHours}
            stats={stats}
            print={printing}
          />

          <View
            dataSet={{ pdf: "logo" }}
            style={tw`flex-row items-center justify-center mt-4`}
          >
            <Text style={tw`text-gray-500 mr-2`}>{t("Powered by")}</Text>
            <AppBrand />
          </View>
        </View>
      </View>
      <Modal transparent visible={printing} animationType="none">
        <View
          style={[
            tw`flex-1 items-center justify-center`,
            { backgroundColor: "rgba(255, 255, 255, 0.92)" },
          ]}
        >
          <AppActivityIndicator />
        </View>
      </Modal>
    </ScrollView>
  );
};

export default EventAnalytics;
